/**
 * « Voir tous mes cours » v2 (route account/visits ; SPEC VC1 à VC3 ; retour M5) — maquette
 * 19-compte-cours.
 *
 * - Cours à venir + listes d'attente : = Visits/VisitsHistory.tsx (GET client/visits,
 *   GET client/waitlist), annulation et retrait de liste d'attente avec les requêtes de la v1.
 * - Cours passés : PAGINÉS (20 par page) avec filtre par année / mois, du plus récent au plus ancien,
 *   via GET client/visits/history (SPEC § 5.3) ; repli calculé dans le navigateur tant que la route
 *   ne répond pas (hooks.ts).
 * - Plus de colonne ni de bouton « Mon spot » (retour M5).
 */
import { useClientWaitlist, useListVisits } from 'hooks/useApiQueries';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ClientVisit } from 'types/client';
import { getLateCancelHours } from 'utils/functions';
import { CancelVisitButton, LeaveWaitlistButton } from './components/VisitActions';
import { AccountShell, BackLink, BrandTag, FootNote, SectionHead, Tag } from './components/Ui';
import { Icon } from './components/Icon';
import { useScrollToHash, useVisitLookup, useVisitsHistory } from './hooks';
import { fmtDateNum, fmtDayLong, fmtTime, monthName, plural, toDate } from './lib/format';
import {
  CONVERTED_VISIT_NOTE,
  HISTORY_PER_PAGE,
  isConvertedVisit,
  OUTCOME_LABEL,
  pageCount,
  periodOptions,
  periodRange,
  upcomingVisits,
  visitName,
  visitOutcome,
  type HistoryPeriod,
} from './lib/visits';
import { waitlistStart, type WaitlistEntry } from './lib/waitlist';

export function MyClasses() {
  useScrollToHash();
  return (
    <AccountShell className="mmv2-acct--page">
      <BackLink />
      <h1 className="mmv2-acct-title mmv2-acct-title--ink">Mes cours</h1>
      <UpcomingTable />
      <PastTable />
    </AccountShell>
  );
}

function UpcomingTable() {
  const { data: visits, isLoading } = useListVisits() || {};
  const { data: waitlist } = useClientWaitlist() || {};
  const { studioOf, coachOf, brandOf, currentSiteId } = useVisitLookup();
  const [cancelledIds, setCancelledIds] = useState<number[]>([]);
  const [leftIds, setLeftIds] = useState<number[]>([]);

  // = VisitsHistory.tsx futureVisits (+ VisitType -1 retiré, comme Visits()).
  const upcoming = useMemo(() => upcomingVisits(visits), [visits]);
  // = WaitlistHistoryItem.tsx : les entrées passées ne sont pas affichées.
  const entries = useMemo(
    () =>
      ((waitlist ?? []) as unknown as WaitlistEntry[])
        .filter((e) => {
          const start = waitlistStart(e);
          return !start || start.getTime() > Date.now();
        })
        .sort((a, b) => (waitlistStart(a)?.getTime() ?? 0) - (waitlistStart(b)?.getTime() ?? 0)),
    [waitlist],
  );

  const hours = getLateCancelHours();

  /*
   * Visites et entrées de liste d'attente dans UNE liste triée par date de début (revue du 04/10 : les entrées venaient
   * après toutes les réservations, jeudi 08/10 « En attente » sous lundi 12/10).
   */
  const rows = useMemo(() => {
    const list: Array<{ kind: 'visit'; visit: ClientVisit; at: number } | { kind: 'entry'; entry: WaitlistEntry; at: number }> = [
      ...upcoming.map((visit) => ({ kind: 'visit' as const, visit, at: toDate(visit.StartDateTime)?.getTime() ?? 0 })),
      ...entries.map((entry) => ({ kind: 'entry' as const, entry, at: waitlistStart(entry)?.getTime() ?? 0 })),
    ];
    return list.sort((a, b) => a.at - b.at);
  }, [upcoming, entries]);

  const renderVisit = (visit: ClientVisit) => {
                  const start = toDate(visit.StartDateTime);
                  const end = toDate(visit.EndDateTime);
                  const cancelled = visit.AppointmentStatus === 'Cancelled' || cancelledIds.includes(visit.Id);
                  const studio = studioOf(visit.SiteId, visit.LocationId);
                  const otherSite = String(visit.SiteId) !== String(currentSiteId);
                  return (
                    <tr key={visit.Id} className={cancelled ? 'mmv2-acct-dim' : undefined}>
                      <td data-label="Nom" className="mmv2-acct-cell-name">
                        <b className="mmv2-acct-upper">{visitName(visit)}</b>
                        {otherSite ? (
                          <span>
                            <BrandTag brand={brandOf(visit.SiteId)} />
                          </span>
                        ) : null}
                      </td>
                      <td data-label="Début" className="mmv2-acct-cell-when">
                        <b>{start ? `${fmtDayLong(start)} ${fmtDateNum(start)}` : '—'}</b>
                        <span>
                          {start ? fmtTime(start) : ''}
                          {start && end ? ` — ${fmtTime(end)}` : ''}
                        </span>
                      </td>
                      <td data-label="Studio">{studio || '—'}</td>
                      <td data-label="Coach">{coachOf(visit.SiteId, visit.StaffId) || '—'}</td>
                      <td data-label="Action / statut" className="mmv2-acct-right mmv2-acct-cell-action">
                        {cancelled ? (
                          <Tag>Annulée</Tag>
                        ) : (
                          <CancelVisitButton
                            visit={visit}
                            studioName={studio}
                            onCancelled={() => setCancelledIds((ids) => [...ids, visit.Id])}
                          />
                        )}
                      </td>
                    </tr>
                  );
                };

  const renderEntry = (entry: WaitlistEntry) => {
                  const start = waitlistStart(entry);
                  const studio = studioOfWaitlist(entry, studioOf);
                  const left = leftIds.includes(entry.Id);
                  return (
                    <tr key={`w-${entry.Id}`} className={left ? 'mmv2-acct-dim' : undefined}>
                      <td data-label="Nom" className="mmv2-acct-cell-name">
                        <b className="mmv2-acct-upper">{entry.ClassSchedule?.ClassDescription?.Name || 'Cours'}</b>
                        <span>
                          <Tag tone="amber">En attente</Tag>
                        </span>
                      </td>
                      <td data-label="Début" className="mmv2-acct-cell-when">
                        <b>{start ? `${fmtDayLong(start)} ${fmtDateNum(start)}` : 'Date non disponible'}</b>
                        <span>{start ? fmtTime(start) : ''}</span>
                      </td>
                      <td data-label="Studio">{studio || '—'}</td>
                      <td data-label="Coach">
                        {coachOf(entry.SiteId ?? Number(currentSiteId), entry.ClassSchedule?.Staff?.Id) || '—'}
                      </td>
                      <td data-label="Action / statut" className="mmv2-acct-right mmv2-acct-cell-action">
                        {left ? (
                          <Tag>Retiré</Tag>
                        ) : (
                          <LeaveWaitlistButton
                            entry={entry}
                            studioName={studio}
                            onLeft={() => setLeftIds((ids) => [...ids, entry.Id])}
                          />
                        )}
                      </td>
                    </tr>
                  );
                };

  return (
    <section className="mmv2-acct-pagesection" aria-labelledby="mmv2-acct-upcoming-all">
      <SectionHead id="mmv2-acct-upcoming-all" title="Cours à venir" />
      {isLoading ? (
        <div className="mmv2-acct-skeleton mmv2-acct-skeleton--table" aria-busy="true" aria-label="Chargement de vos cours" />
      ) : upcoming.length || entries.length ? (
        <>
          <div className="mmv2-card mmv2-acct-tablecard">
            <table className="mmv2-acct-dtable mmv2-acct-dtable--visits">
              <thead>
                <tr>
                  <th scope="col">Nom</th>
                  <th scope="col">Début</th>
                  <th scope="col">Studio</th>
                  <th scope="col">Coach</th>
                  <th scope="col" className="mmv2-acct-right">
                    Action / statut
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (row.kind === 'visit' ? renderVisit(row.visit) : renderEntry(row.entry)))}
              </tbody>
            </table>
          </div>
          {upcoming.length ? (
            <FootNote>
              Annulation gratuite jusqu’à {hours}&nbsp;h avant le cours. Passé ce délai, le crédit n’est pas recrédité.
            </FootNote>
          ) : null}
        </>
      ) : (
        <div className="mmv2-card mmv2-card--pad mmv2-acct-empty">
          <p className="mmv2-muted">Rien de réservé pour l’instant.</p>
          <Link className="mmv2-btn mmv2-btn--cta mmv2-btn--sm" to="/">
            Réserver un cours
          </Link>
        </div>
      )}
    </section>
  );
}

/**
 * = VisitsHistory.tsx WaitlistEntries : le studio d'une entrée de liste d'attente est cherché par son
 * seul Id dans la v1 ; on préfère Id + site quand l'entrée porte son site.
 */
function studioOfWaitlist(entry: WaitlistEntry, studioOf: (siteId: number, locationId?: number) => string) {
  const locationId = entry.ClassSchedule?.Location?.Id;
  const siteId = entry.SiteId ?? Number(typeof mondayMindbody !== 'undefined' ? mondayMindbody?.siteId : 0);
  return studioOf(siteId, locationId);
}

function PastTable() {
  const [period, setPeriod] = useState<HistoryPeriod>({ year: '', month: '' });
  const [page, setPage] = useState(1);
  const range = periodRange(period);
  const { data, isLoading, isFetching } = useVisitsHistory({ page, perPage: HISTORY_PER_PAGE, ...range });
  const { studioOf, coachOf, brandOf, currentSiteId } = useVisitLookup();
  const sectionRef = useRef<HTMLElement>(null);
  const firstRender = useRef(true);

  // Les mois proposés : ceux de la première réponse sans filtre (la liste ne doit pas se réduire au filtre choisi).
  const [months, setMonths] = useState<string[]>([]);
  useEffect(() => {
    if (data?.months?.length && (!period.year || months.length === 0)) setMonths(data.months);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.months]);
  const options = useMemo(() => periodOptions(months), [months]);

  const total = data?.total ?? 0;
  const pages = pageCount(total, HISTORY_PER_PAGE);
  const items: ClientVisit[] = data?.items ?? [];

  // Changement de page : on remonte en haut du tableau (pas au premier rendu).
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [page]);

  const setYear = (year: string) => {
    setPeriod({ year, month: '' });
    setPage(1);
  };
  // « Effacer » disparaît au clic : le focus revient sur la liste des années (il tombait sur BODY, revue du 04/10).
  const clearPeriod = () => {
    setYear('');
    window.setTimeout(() => document.getElementById('mmv2-acct-year')?.focus(), 0);
  };
  const setMonth = (month: string) => {
    setPeriod((p) => ({ ...p, month }));
    setPage(1);
  };

  const periodLabel = period.year
    ? period.month
      ? `${monthName(Number(period.month))} ${period.year}`
      : period.year
    : '';

  return (
    <section className="mmv2-acct-pagesection" id="cours-passes" ref={sectionRef} aria-labelledby="mmv2-acct-past">
      <div className="mmv2-acct-sechead mmv2-acct-sechead--filters">
        <h2 className="mmv2-acct-kicker" id="mmv2-acct-past">
          Cours passés
        </h2>
        {options.years.length ? (
          <div className="mmv2-acct-period" role="group" aria-label="Filtrer par période">
            <label className="mmv2-acct-sr" htmlFor="mmv2-acct-year">
              Année
            </label>
            <select id="mmv2-acct-year" className="mmv2-acct-select" value={period.year} onChange={(e) => setYear(e.target.value)}>
              <option value="">Toutes les années</option>
              {options.years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <label className="mmv2-acct-sr" htmlFor="mmv2-acct-month">
              Mois
            </label>
            <select
              id="mmv2-acct-month"
              className="mmv2-acct-select"
              value={period.month}
              onChange={(e) => setMonth(e.target.value)}
              disabled={!period.year}
            >
              <option value="">Tous les mois</option>
              {(options.monthsByYear[period.year] ?? []).map((m) => (
                <option key={m} value={m}>
                  {monthName(Number(m))}
                </option>
              ))}
            </select>
            {period.year ? (
              <button type="button" className="mmv2-linkish" onClick={clearPeriod}>
                Effacer
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {isLoading ? (
        <div className="mmv2-acct-skeleton mmv2-acct-skeleton--table" aria-busy="true" aria-label="Chargement de votre historique" />
      ) : items.length ? (
        <>
          <p className="mmv2-acct-total mmv2-small mmv2-muted" aria-live="polite">
            {plural(total, 'cours', 'cours')}
            {periodLabel ? ` en ${periodLabel}` : ''}
            {pages > 1 ? ` · page ${page} sur ${pages}` : ''}
          </p>
          <div className={`mmv2-card mmv2-acct-tablecard${isFetching ? ' mmv2-acct-tablecard--busy' : ''}`} aria-busy={isFetching}>
            <table className="mmv2-acct-dtable mmv2-acct-dtable--visits">
              <thead>
                <tr>
                  <th scope="col">Nom</th>
                  <th scope="col">Date</th>
                  <th scope="col">Studio</th>
                  <th scope="col">Coach</th>
                  <th scope="col" className="mmv2-acct-right">
                    Statut
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((visit) => {
                  const start = toDate(visit.StartDateTime);
                  const outcome = OUTCOME_LABEL[visitOutcome(visit)];
                  const otherSite = String(visit.SiteId) !== String(currentSiteId);
                  return (
                    <tr key={visit.Id}>
                      <td data-label="Nom" className="mmv2-acct-cell-name">
                        <b className="mmv2-acct-upper">{visitName(visit)}</b>
                        {otherSite ? (
                          <span>
                            <BrandTag brand={brandOf(visit.SiteId)} />
                          </span>
                        ) : null}
                      </td>
                      <td data-label="Date" className="mmv2-acct-cell-when">
                        {start ? `${fmtDateNum(start)} — ${fmtTime(start)}` : '—'}
                      </td>
                      {isConvertedVisit(visit) ? (
                        <td data-label="Studio" colSpan={2} className="mmv2-muted">
                          {CONVERTED_VISIT_NOTE}
                        </td>
                      ) : (
                        <>
                          <td data-label="Studio">{studioOf(visit.SiteId, visit.LocationId) || '—'}</td>
                          <td data-label="Coach">{coachOf(visit.SiteId, visit.StaffId) || '—'}</td>
                        </>
                      )}
                      <td data-label="Statut" className="mmv2-acct-right mmv2-acct-cell-status">
                        {outcome ? <Tag tone={outcome.tone}>{outcome.label}</Tag> : <span className="mmv2-muted">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pages > 1 ? (
            <nav className="mmv2-acct-pager" aria-label="Pages de l’historique">
              <button
                type="button"
                className="mmv2-iconbtn"
                // aria-disabled et non disabled : le bouton qui a le focus le garde quand il devient inactif (première ou
                // dernière page atteinte au clavier ; le focus tombait sur BODY, revue du 05/10).
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-disabled={page <= 1 || undefined}
                aria-label="Page précédente"
              >
                <Icon name="left" size={16} />
              </button>
              <span className="mmv2-acct-pager__label">
                Page <b>{page}</b> sur <b>{pages}</b>
              </span>
              <button
                type="button"
                className="mmv2-iconbtn"
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                aria-disabled={page >= pages || undefined}
                aria-label="Page suivante"
              >
                <Icon name="right" size={16} />
              </button>
            </nav>
          ) : null}
        </>
      ) : (
        <div className="mmv2-card mmv2-card--pad">
          <p className="mmv2-muted">
            {period.year ? `Aucun cours passé en ${periodLabel}.` : 'Aucun cours passé pour l’instant.'}
          </p>
        </div>
      )}
    </section>
  );
}
